/**
 * Auras (ออร่า): the light a fighter carries. Which aura shows is data; the client draws each
 * style (render/auras.ts) and never decides who deserves one.
 *
 * The looks are the twenty of the aura reference (docs/art/auras-ref, `AURA_LIST`). Each source
 * picks one of them:
 * - A standing aura, one at a time, strongest first: a title's (data/titles `aura`, else
 *   `TITLE_AURAS`), the active neigong's once mastered (data/neigong `aura`), or the power realm
 *   reached (`REALM_AURAS`). Fellow wanderers show the neigong they have mastered (data/party
 *   `mastered`).
 * - Over it, while it lasts: rage full and a power move ready (`RAGE_AURA`), sitting in
 *   meditation (`MEDITATION_AURA`).
 * - Bosses carry their own (data/monsters `aura`).
 * - A move just used makes the qi flare in the art's colour; a forged weapon shows on the blade
 *   (data/forge). Neither is listed here.
 */
import { REALMS_TO_COME } from '../cultivation';
import type { AuraStyle } from './neigong';

/**
 * Every look the client can draw: the neigong styles, the rest, and the looks the reference added
 * (wind, thunder, moon, frost, guardian), which the 2D client draws as the nearest older look until
 * the 3D effects exist.
 */
export type AuraLook =
  | AuraStyle
  | 'swordink'
  | 'realm'
  | 'rage'
  | 'meditate'
  | 'demon'
  | 'wind'
  | 'thunder'
  | 'moon'
  | 'frost'
  | 'guardian';

export interface AuraDef {
  readonly style: AuraLook;
  /** Its colour (rgb, natural dye or light, not a pure hue). */
  readonly color: string;
  /** How grand within its style (1 = the least): realms and bosses grow with it. */
  readonly tier?: number;
}

/** One aura of the reference: its name, its look and its colour. */
export interface AuraRef {
  readonly id: string;
  readonly name: string;
  readonly look: AuraLook;
  readonly color: string;
  /** What the sheet shows, for the 3D effects: the ring, the motes, the large form. */
  readonly note: string;
}

const list: AuraRef[] = [
  {
    id: 'blazing-fire',
    name: 'อัคนีเพลิง',
    look: 'flame',
    color: '216,50,58',
    note: 'flames wrap the body, an ember ring, fire pillars',
  },
  {
    id: 'flowing-water',
    name: 'วารีธารา',
    look: 'mist',
    color: '84,150,226',
    note: 'blue water ribbons and bubbles, ripple rings',
  },
  {
    id: 'whirlwind',
    name: 'วายุหมุน',
    look: 'wind',
    color: '96,206,170',
    note: 'jade-green wind spirals, swirling rings',
  },
  {
    id: 'earth',
    name: 'พสุธา',
    look: 'bell',
    color: '200,156,96',
    note: 'floating rocks and dust, a crack ring',
  },
  {
    id: 'lightning',
    name: 'สายฟ้า',
    look: 'thunder',
    color: '150,96,232',
    note: 'violet arcs, a crackling ring, a bolt pillar',
  },
  {
    id: 'sun',
    name: 'สุริยันต์',
    look: 'realm',
    color: '236,198,112',
    note: 'a golden sunburst halo, stars',
  },
  {
    id: 'moon',
    name: 'จันทรา',
    look: 'moon',
    color: '176,170,240',
    note: 'a pale moon disc behind, clouds, sparkles',
  },
  {
    id: 'falling-blossoms',
    name: 'ดอกไม้ร่วง',
    look: 'petals',
    color: '214,104,180',
    note: 'pink petals spiral, a petal ring',
  },
  {
    id: 'eternal-ice',
    name: 'น้ำแข็งนิรันดร์',
    look: 'frost',
    color: '150,206,246',
    note: 'ice crystal spikes, snowflakes',
  },
  {
    id: 'demon-blood',
    name: 'โลหิตมาร',
    look: 'demon',
    color: '150,36,52',
    note: 'red and black smoke, blood wisps',
  },
  {
    id: 'jade',
    name: 'หยกเขียว',
    look: 'taiji',
    color: '96,196,120',
    note: 'green leaf flames, jade sigils',
  },
  {
    id: 'violet-mystery',
    name: 'ม่วงปริศนา',
    look: 'ink',
    color: '120,72,184',
    note: 'purple runes, a dark sigil ring',
  },
  {
    id: 'gold',
    name: 'ทองคำ',
    look: 'blades',
    color: '240,192,64',
    note: 'gold coins and sigils in a ring',
  },
  {
    id: 'flame-phoenix',
    name: 'หงส์เพลิง',
    look: 'rage',
    color: '214,86,44',
    note: 'orange phoenix wings rising',
  },
  {
    id: 'jade-dragon',
    name: 'มังกรเมฆคราม',
    look: 'rage',
    color: '86,150,230',
    note: 'a blue dragon coiling round',
  },
  {
    id: 'mist-cloud',
    name: 'หมอกเมฆา',
    look: 'mist',
    color: '220,236,255',
    note: 'grey clouds rolling at the feet',
  },
  {
    id: 'earth-guardian',
    name: 'ปักษาพสุธา',
    look: 'guardian',
    color: '110,200,96',
    note: 'green bird wings and leaves',
  },
  {
    id: 'spirit',
    name: 'วิญญาณ',
    look: 'meditate',
    color: '160,110,236',
    note: 'violet will-o-wisps',
  },
  {
    id: 'white-flame',
    name: 'นภาเพลิงขาว',
    look: 'realm',
    color: '246,236,206',
    note: 'white-gold flame with a halo',
  },
  {
    id: 'blood-blossom',
    name: 'โลหิตบาน',
    look: 'bloodstorm',
    color: '220,30,40',
    note: 'red petals and blood flowers',
  },
];

export const AURA_LIST: readonly AuraRef[] = list;
export const AURAS: Readonly<Record<string, AuraRef>> = Object.fromEntries(list.map((a) => [a.id, a]));

/** A reference aura as a source carries it, at a tier. */
export function auraOf(id: string, tier?: number): AuraDef {
  const a = AURAS[id];
  if (!a) throw new Error(`Unknown aura: ${id}`);
  return tier === undefined ? { style: a.look, color: a.color } : { style: a.look, color: a.color, tier };
}

/**
 * The aura of each power realm (data order of `REALMS` in cultivation.ts): none below
 * ยอดฝีมือชั้นหนึ่ง, then the sun, growing to its full halo at ปรมาจารย์.
 */
export const REALM_AURAS: readonly (AuraDef | null)[] = [
  null,
  null,
  auraOf('whirlwind', 1),
  auraOf('whirlwind', 2),
  auraOf('sun', 1),
  auraOf('sun', 2),
  auraOf('sun', 3),
];

/** The realms to come (`REALMS_TO_COME`), when each opens: the white flame, grander each time. */
export const REALM_AURAS_TO_COME: readonly AuraDef[] = REALMS_TO_COME.map((_, i) =>
  auraOf('white-flame', i + 1),
);

/** Rage full (a power move ready): the flame phoenix, heat shimmering off the body. */
export const RAGE_AURA: AuraDef = auraOf('flame-phoenix');

/** Sitting in meditation: spirit lights rising round a lotus on the ground. */
export const MEDITATION_AURA: AuraDef = auraOf('spirit');

/**
 * Titles that carry an aura, by title id, for titles whose own data has none (a title's own
 * `aura` comes first). The looks the reference added go to titles, never to a neigong: the
 * mastery motes (client render/effects) know only the older looks.
 */
export const TITLE_AURAS: Readonly<Record<string, AuraDef>> = {
  'wolf-hunter': auraOf('moon'),
  's1-wanderer': auraOf('whirlwind'),
  's1-sovereign': auraOf('eternal-ice'),
  'heaven-ladder': auraOf('lightning'),
  'hell-ladder': auraOf('demon-blood'),
  legend: auraOf('jade-dragon'),
  'neigong-master': auraOf('earth-guardian'),
  'arena-champion': auraOf('gold'),
};
